import * as Comlink from 'comlink';
import type { QueryWorkerApi } from '@/workers/query.worker';
import { recordCrash, type CrashTracker } from './crash-tracker';

/**
 * Main-thread handle to the query worker, plus crash recovery.
 *
 * On `error` / `messageerror` from the underlying Worker we terminate the
 * dead instance, spawn a fresh one, and bump a generation counter. Hooks
 * subscribed via `subscribeToWorkerGeneration` re-run their queries —
 * the worker's in-memory cache is cold-started, so this is a transparent
 * recovery from the user's point of view.
 *
 * To prevent an infinite respawn loop on a poison query, the tracker
 * stops respawning after 3 crashes inside 10 seconds.
 */

type WorkerListener = (generation: number) => void;

let remote: Comlink.Remote<QueryWorkerApi> | null = null;
let worker: Worker | null = null;
let generation = 0;
let poisoned = false;
const tracker: CrashTracker = { events: [] };
const listeners = new Set<WorkerListener>();

function spawn(): void {
  worker = new Worker(
    new URL('../workers/query.worker.ts', import.meta.url),
    { type: 'module', name: 'query-worker' },
  );
  worker.addEventListener('error', handleCrash);
  worker.addEventListener('messageerror', handleCrash);
  remote = Comlink.wrap<QueryWorkerApi>(worker);
}

function handleCrash(event: Event): void {
  // Surface to the console so operators can see what blew up; the UI shows
  // a brief loading spinner during the respawn and then re-renders.
  console.error('[query-worker] crashed:', event);
  const result = recordCrash(tracker, Date.now());
  if (result.poisoned) {
    poisoned = true;
    console.error(
      '[query-worker] poisoned — too many recent crashes, will not respawn',
    );
    worker?.terminate();
    worker = null;
    remote = null;
    return;
  }
  worker?.removeEventListener('error', handleCrash);
  worker?.removeEventListener('messageerror', handleCrash);
  worker?.terminate();
  worker = null;
  remote = null;
  spawn();
  generation++;
  for (const l of listeners) l(generation);
}

export function getQueryWorker(): Comlink.Remote<QueryWorkerApi> {
  if (poisoned) {
    throw new Error(
      'Query worker is poisoned: 3+ crashes inside 10s. Reload the page to retry.',
    );
  }
  if (!remote) spawn();
  return remote!;
}

/**
 * Subscribe to worker-respawn events. The callback is invoked with the new
 * generation number after every successful respawn. Returns an unsubscribe
 * function. Used by `useQueryResult` to retry queries after a crash.
 */
export function subscribeToWorkerGeneration(cb: WorkerListener): () => void {
  listeners.add(cb);
  return (): void => {
    listeners.delete(cb);
  };
}

/** Current worker generation. Bumped on every successful respawn. */
export function getWorkerGeneration(): number {
  return generation;
}

/**
 * Test-only helper. Resets module-level state so individual unit tests
 * don't leak into each other. Not exported via barrel — tests import the
 * file directly.
 */
export function _resetWorkerClientForTests(): void {
  worker?.terminate();
  worker = null;
  remote = null;
  generation = 0;
  poisoned = false;
  tracker.events = [];
  listeners.clear();
}

/**
 * Used by `clearAllStorage` (D-027) to drop every DuckDB table held by
 * the worker. Terminating is the cheapest way: the next `getQueryWorker`
 * call respawns a fresh instance with no state. Bumps generation so any
 * subscribed widget re-runs its query (and gets an empty result, since
 * the dataset is gone).
 */
export function _terminateWorkerForCleanup(): void {
  if (!worker) return;
  worker.removeEventListener('error', handleCrash);
  worker.removeEventListener('messageerror', handleCrash);
  worker.terminate();
  worker = null;
  remote = null;
  generation++;
  for (const l of listeners) l(generation);
}
